/* eslint-disable no-console */
import React, { useEffect, useState } from "react";

import { BarsProvider } from "./actions/bars";
import { PromotionsProvider } from "./actions/promotions";
import { CategoriesProvider } from "./actions/categories";
import { UsersProvider } from "./actions/users";

import { auth, firestore } from "./services/firebase";
import { LoadingBlanket } from "./components/LoadingBlanket";
import { captureError } from "./lib/errorReporting";
import { StateRegionSubregionProvider } from "./actions/stateRegions";
import { PromotionPlansProvider } from "./actions/promotionPlans";
import { FeaturePlacementsProvider } from "./actions/featurePlacements";
import { LocalAdsProvider } from "./actions/localAds";

/**
 * this is where we can add context providers for global state
 */

const UserRolesContext = React.createContext({
	isAdmin: false,
	roles: {},
	status: "fetching"
});

export function StateProvider(props) {
	const [isAuthReady, setIsAuthReady] = useState(false);
	const [userRoles, setUserRoles] = useState({
		isAdmin: false,
		roles: {},
		status: "fetching"
	});

	const getRoles = () => {
		let unsub = { current: () => {} };

		auth.onAuthStateChanged((user) => {
			setIsAuthReady(true);
			if (!user?.uid) {
				setUserRoles({ isAdmin: false, roles: {}, status: "idle" });
				if (unsub.current && typeof unsub.current === "function") {
					unsub.current();
				}
				return;
			}

			const usersCollection = firestore.collection("Users");
			// Load the Users doc by Auth uid (the canonical key). Querying by
			// email could return a stale duplicate doc — an older `.add()` bug
			// created random-id docs per email — which may be missing `isAdmin`
			// or `roles`, silently dropping admins to the claim-bar flow. Keying
			// by uid matches signIn() and the security rules
			// (get(/Users/$(request.auth.uid))).
			const userRef = usersCollection.doc(user.uid);

			userRef
				.get()
				.then((doc) => {
					if (!doc.exists) {
						// Create the Users doc keyed by the Auth uid so it matches
						// security-rule lookups (get(/Users/$(request.auth.uid))).
						return userRef.set({ email: user.email }, { merge: true }).then(() => {
							setUserRoles({ id: user.uid, email: user.email, isAdmin: false, roles: {}, status: "idle" });
						});
					}
					setUserRoles({ id: doc.id, ...doc.data(), status: "idle" });
				})
				.catch((error) => {
					console.error(`Error fetching roles: ${error}`);
					captureError(error, { action: "fetchUserRoles" });
				});

			unsub.current = userRef.onSnapshot(
				(doc) => {
					if (!doc.exists) return;
					setUserRoles({ id: doc.id, ...doc.data(), status: "idle" });
				},
				(error) => {
					console.error(`Error updating roles: ${error}`);
					captureError(error, { action: "userRolesSnapshot" });
				}
			);
		});

		return () => {
			if (unsub.current && typeof unsub.current === "function") {
				unsub.current();
			}
		};
	};

	useEffect(() => {
		return getRoles();
	}, []);

	if (!isAuthReady) return <LoadingBlanket />;

	return (
		<UserRolesContext.Provider value={{ userRoles, getRoles }}>
			<LocalAdsProvider>
				<FeaturePlacementsProvider>
					<CategoriesProvider>
						<UsersProvider>
							<StateRegionSubregionProvider>
								<PromotionPlansProvider>
									<PromotionsProvider>
										<BarsProvider>
											{props.children}
										</BarsProvider>
									</PromotionsProvider>
								</PromotionPlansProvider>
							</StateRegionSubregionProvider>
						</UsersProvider>
					</CategoriesProvider>
				</FeaturePlacementsProvider>
			</LocalAdsProvider>
		</UserRolesContext.Provider>
	);
}

export const useUserRoles = () => React.useContext(UserRolesContext);
